import { createContext, useMemo } from "react";
import { Route, Switch } from "react-router-dom";
import { QueryClientProvider, QueryClient } from "react-query";
import { withAuth } from "../msal/MsalAuthProvider";

import {
    ActionsGrid,
    EmployeeList,
    Inspection,
    InspectionDetails,
    Workshop,
    Equipment,
    WorkshopOrder,
    SpareParts,
    MyList,
    Gardening,
    GardeningClock,
    Mowing,
    MowingClock,
    ReportBroken,
    WinterWork,
    WinterWorkClock,
    TimeClock,
    PhotoAlbumPage,
    NotFound,
    Warehouse,
    PickingLineDetails,
    ReportIncident,
} from "../pages";

const queryClient = new QueryClient();

export const UserDataContext = createContext({
    user: null,
    userContextData: {},
    setUserContextData: () => {},
    clearUserContextData: () => {},
});

function App({ auth }) {
    const user = useMemo(
        () => ({
            id: auth.crmId,
            name: auth.crmName,
            lang: auth.language,
            crmPrivileges: auth.crmPrivileges,
            azureObjectIdToken: auth.user.idToken.oid,
            isKiosk: auth.crmPrivileges.some((p) => p.name.toLowerCase() === "kiosk"),
        }),
        [auth]
    );

    const kioskRoutes = (
        <Switch>
            <Route exact path="/" component={EmployeeList} />
            <Route exact path="/timeclock/:id/:name" component={TimeClock} />
        </Switch>
    );

    const appRoutes = (
        <Switch>
            <Route exact path="/" component={ActionsGrid} />
            <Route exact path="/timeclock" component={TimeClock} />
            <Route exact path="/winterwork" component={WinterWork} />
            <Route exact path="/winterwork/:id" render={(props) => <WinterWorkClock {...props} user={user} crmPrivileges={user.crmPrivileges} />} />
            <Route exact path="/gardening" render={(props) => <Gardening {...props} user={user} crmPrivileges={user.crmPrivileges} />} />
            <Route exact path="/gardening/:id" component={GardeningClock} />
            <Route exact path="/mowing" render={(props) => <Mowing {...props} user={user} crmPrivileges={user.crmPrivileges} />} />
            <Route exact path="/mowing/:id" component={MowingClock} />
            <Route exact path="/inspection" component={Inspection} />
            <Route exact path="/inspection/:id" component={InspectionDetails} />
            <Route exact path="/report/incident/:id?" component={ReportIncident} />
            <Route exact path="/report/broken" render={(props) => <ReportBroken {...props} user={user} crmPrivileges={user.crmPrivileges} />} />
            <Route exact path="/photos/:id" render={(props) => <PhotoAlbumPage {...props} user={user} crmPrivileges={user.crmPrivileges} />} />
            <Route exact path="/workshop" component={Workshop} />
            <Route exact path="/workshop/mylist" component={MyList} />
            <Route exact path="/workshop/:equipmentId" component={Equipment} />
            <Route exact path="/workshop/:equipmentId/:workshopOrderId" component={WorkshopOrder} />
            <Route exact path="/workshop/:equipmentId/:workshopOrderId/:workshopLineId/spareparts" component={SpareParts} />
            <Route exact path="/warehouse" component={Warehouse} />
            <Route exact path="/warehouse/:id" component={PickingLineDetails} />
            <Route component={NotFound} />
        </Switch>
    );

    return (
        <div className="App">
            <QueryClientProvider client={queryClient}>
                <UserDataContext.Provider value={{ user }}>
                    <>{user.isKiosk ? kioskRoutes : appRoutes}</>
                </UserDataContext.Provider>
            </QueryClientProvider>
        </div>
    );
}

export default withAuth(App);
